import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {
  CloudWatchAlarmResponse,
  CloudWatchAlarmState,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {ViewField} from 'src/design/atoms/ViewField';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  formatRelativeOrNever,
  formatUtcDateTime,
} from 'src/shared/utils/formatters';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {
  ALARM_STATE_META,
  formatMetricIdentity,
  formatPeriod,
  formatThreshold,
} from '../cloudWatchDisplay';

export interface CloudWatchAlarmsTableProps {
  alarms: CloudWatchAlarmResponse[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: string;
  stateFilter?: CloudWatchAlarmState;
  onRefresh: () => void | Promise<unknown>;
}

export const CloudWatchAlarmsTable = ({
  alarms,
  isLoading,
  isFetching,
  isError,
  error,
  stateFilter,
  onRefresh,
}: CloudWatchAlarmsTableProps) => {
  const rows = useMemo(
    () =>
      stateFilter === undefined
        ? alarms
        : alarms.filter(alarm => alarm.state === stateFilter),
    [alarms, stateFilter]
  );

  const columns = useMemo<MRT_ColumnDef<CloudWatchAlarmResponse>[]>(
    () => [
      {
        id: 'state',
        header: 'State',
        size: 150,
        accessorFn: row => ALARM_STATE_META[row.state].label,
        Cell: ({row}) => {
          const meta = ALARM_STATE_META[row.original.state];
          return <Pill text={meta.label} color={meta.color} />;
        },
      },
      {
        header: 'Alarm',
        size: 280,
        accessorKey: 'label',
        Cell: ({row}) => (
          <Stack gap={0}>
            <Text size="sm" fw={500}>
              {row.original.label}
            </Text>
            {row.original.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {row.original.description}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        id: 'metric',
        header: 'Metric',
        size: 240,
        accessorFn: row => formatMetricIdentity(row.namespace, row.metricName),
        Cell: ({row}) => (
          <Stack gap={0}>
            <Text size="sm">
              {formatMetricIdentity(
                row.original.namespace,
                row.original.metricName
              )}
            </Text>
            <Text size="xs" c="dimmed">
              {row.original.statistic ?? '—'} ·{' '}
              {formatPeriod(row.original.periodSeconds)}
            </Text>
          </Stack>
        ),
      },
      {
        id: 'threshold',
        header: 'Threshold',
        size: 140,
        enableColumnFilter: false,
        accessorFn: row => row.threshold ?? 0,
        Cell: ({row}) => (
          <Text size="sm">
            {formatThreshold(
              row.original.comparisonOperator,
              row.original.threshold
            )}
          </Text>
        ),
      },
      {
        id: 'stateUpdatedAt',
        header: 'Last Change',
        size: 150,
        enableColumnFilter: false,
        accessorFn: row =>
          row.stateUpdatedAt ? parseUtcDate(row.stateUpdatedAt).getTime() : 0,
        Cell: ({row}) => (
          <Tooltip
            label={formatUtcDateTime(row.original.stateUpdatedAt)}
            withArrow>
            <Text size="sm" c="dimmed">
              {formatRelativeOrNever(row.original.stateUpdatedAt)}
            </Text>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'stateReason',
        header: 'Reason',
        size: 320,
        Cell: ({row}) => (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {row.original.stateReason ?? '—'}
          </Text>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'cloudwatch-alarms',
    columns,
    defaultDensity: 'xs',
    defaultSorting: [{id: 'state', desc: false}],
    defaultPagination: {pageIndex: 0, pageSize: 25},
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableRowActions: false,
      columns,
      data: rows,
      getRowId: row => row.name,
      renderDetailPanel: ({row}) => (
        <Stack gap="sm" px="xl" py="md">
          <ViewField label="Alarm name" value={row.original.name} />
          <ViewField
            label="Reason"
            value={row.original.stateReason ?? 'No reason reported.'}
          />
          <Group gap="xl" align="flex-start">
            <ViewField
              label="Evaluation periods"
              value={
                row.original.evaluationPeriods != null
                  ? String(row.original.evaluationPeriods)
                  : '—'
              }
            />
            <ViewField label="Unit" value={row.original.unit ?? '—'} />
            <ViewField
              label="Dimensions"
              value={
                (row.original.dimensions ?? []).length > 0
                  ? (row.original.dimensions ?? [])
                      .map(d => `${d.name}=${d.value}`)
                      .join(', ')
                  : 'None'
              }
            />
          </Group>
        </Stack>
      ),
      renderEmptyRowsFallback: () => (
        <Text c="dimmed" ta="center" py="xl" size="sm">
          No alarms match this state. Alarms are defined in the backend CDK
          stack.
        </Text>
      ),
    },
    totalCount: rows.length,
    isLoading,
    isFetching,
    isError,
    error,
    onRefresh,
  });

  return table;
};
